import React, { useState } from 'react';
import jp from 'jsonpath';
import { useWizard, WizardStepLayout } from 'components/layout/wizards';
import { SelectField, BaseTextInput, BaseTextArea, InputLabel, ToggleField } from 'components/forms';
import { Button, PrimaryButton } from 'components/buttons';
import { AlertSuccess, AlertError } from 'components/layout/alerts';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { formatWorkerTestContent } from 'utils/workers';
import {
  WORKER_PARSER_VALIDATION_TYPES,
  WORKER_PARSER_VALIDATION_TYPES_OPTIONS,
  WORKER_PATH_OPERATIONS,
  WORKER_PATH_OPERATIONS_OPTIONS
} from 'constants/workers';

const parseNumericValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const evaluateCondition = (inputValue, operation, controlValue) => {
  switch (operation) {
    case WORKER_PATH_OPERATIONS.GT:
    case WORKER_PATH_OPERATIONS.GTE:
    case WORKER_PATH_OPERATIONS.LT:
    case WORKER_PATH_OPERATIONS.LTE: {
      const a = parseNumericValue(inputValue);
      const b = parseNumericValue(controlValue);
      if (a === null || b === null) return false;
      if (operation === WORKER_PATH_OPERATIONS.GT) return a > b;
      if (operation === WORKER_PATH_OPERATIONS.GTE) return a >= b;
      if (operation === WORKER_PATH_OPERATIONS.LT) return a < b;
      return a <= b;
    }
    case WORKER_PATH_OPERATIONS.EQ:
      return String(inputValue) === String(controlValue);
    case WORKER_PATH_OPERATIONS.NEQ:
      return String(inputValue) !== String(controlValue);
    case WORKER_PATH_OPERATIONS.CONTAINS: {
      if (Array.isArray(inputValue)) {
        return inputValue.some((item) => String(item) === String(controlValue));
      }
      return String(inputValue).includes(String(controlValue));
    }
    default:
      return true;
  }
};

export function ParserValidationForm({
  initialValues = {},
  testResultData = null,
  onSubmit,
  onCancel,
  isWizard = false,
  saving = false
}) {
  const [validationType, setValidationType] = useState(initialValues.validationType || WORKER_PARSER_VALIDATION_TYPES.REGEX);
  const [expression, setExpression] = useState(initialValues.expression || '');

  const hasInitialCondition = Boolean(initialValues.operation && initialValues.operation !== WORKER_PATH_OPERATIONS.NONE);
  const [conditionEnabled, setConditionEnabled] = useState(hasInitialCondition);
  const [operation, setOperation] = useState(initialValues.operation || WORKER_PATH_OPERATIONS.GT);
  const [controlValue, setControlValue] = useState(initialValues.controlValue ?? '');

  const [testContent, setTestContent] = useState(() => formatWorkerTestContent(testResultData));
  const [testing, setTesting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);

  const effectiveOperation = conditionEnabled ? operation : WORKER_PATH_OPERATIONS.NONE;

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setValidationResult(null);

    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      if (validationType === WORKER_PARSER_VALIDATION_TYPES.REGEX) {
        try {
          let pattern = expression;
          let flags = '';

          if (expression.startsWith('/') && expression.lastIndexOf('/') > 0) {
            const lastSlashIndex = expression.lastIndexOf('/');
            const potentialFlags = expression.substring(lastSlashIndex + 1);

            if (/^[gimsuy]*$/.test(potentialFlags)) {
              pattern = expression.substring(1, lastSlashIndex);
              flags = potentialFlags;
            }
          }

          const regex = new RegExp(pattern, flags);
          const matchResult = regex.exec(testContent);

          if (!matchResult) {
            throw new Error('No match found for the given regex.');
          }

          if (conditionEnabled) {
            const inputValue = matchResult[1] ?? matchResult[0];
            const passed = evaluateCondition(inputValue, operation, controlValue);
            setValidationResult({
              match: passed,
              value: matchResult[0],
              groups: matchResult.slice(1),
              conditionInput: inputValue,
              conditionResult: passed
            });
            if (!passed) {
              throw new Error(`Condition failed: "${inputValue}" is not ${operation} "${controlValue}"`);
            }
          } else {
            setValidationResult({
              match: true,
              value: matchResult[0],
              groups: matchResult.slice(1)
            });
          }
        } catch (e) {
          if (!validationResult) {
            throw new Error(`Regex Error: ${e.message}`);
          }
          throw e;
        }
      } else if (validationType === WORKER_PARSER_VALIDATION_TYPES.JSON) {
        let jsonData;
        try {
          jsonData = JSON.parse(testContent);
        } catch (_e) {
          throw new Error('Invalid JSON content. Please check your test content.');
        }

        const results = jp.query(jsonData, expression);

        if (!results || results.length === 0) {
          throw new Error('No match found for the given JSON path.');
        }

        if (conditionEnabled) {
          const matchedResults = results.filter(
            (result) => evaluateCondition(result, operation, controlValue)
          );

          setValidationResult({
            match: matchedResults.length > 0,
            value: matchedResults.length > 0 ? matchedResults : results,
            count: matchedResults.length,
            totalCount: results.length,
            conditionResult: matchedResults.length > 0
          });

          if (matchedResults.length === 0) {
            throw new Error(
              `Condition failed: none of the ${results.length} result(s) matched ${operation} "${controlValue}"`
            );
          }
        } else {
          setValidationResult({
            match: true,
            value: results,
            count: results.length
          });
        }
      }
    } catch (err) {
      setError(err.message || 'Validation failed');
    } finally {
      setTesting(false);
    }
  };

  const handleResetContent = () => {
    setTestContent(formatWorkerTestContent(testResultData));
    setValidationResult(null);
    setError(null);
  };

  const handleProceed = () => {
    onSubmit({
      validationType,
      expression,
      operation: effectiveOperation,
      controlValue: conditionEnabled ? controlValue : undefined
    });
  };

  const formContent = (
    <>
      {
        (validationResult || error) && (
          <div className='mb-8'>
            {validationResult && !error && (
              <AlertSuccess title="Match Found">
                <pre className="whitespace-pre-wrap text-xs mt-2">
                  {JSON.stringify(validationResult, null, 2)}
                </pre>
              </AlertSuccess>
            )}

            {error && (
              <AlertError title="No Match / Error">
                {error}
              </AlertError>
            )}
          </div>
        )
      }

      <div className="space-y-6">
        <SelectField
          label="Validation Type"
          value={validationType}
          onChange={setValidationType}
          options={WORKER_PARSER_VALIDATION_TYPES_OPTIONS}
        />

        <div>
          <InputLabel htmlFor="expression">
            {validationType === WORKER_PARSER_VALIDATION_TYPES.REGEX ? 'Regex Expression' : 'JSON Path Expression'}
          </InputLabel>
          <div className="mt-2">
            <BaseTextInput
              id="expression"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              placeholder={validationType === WORKER_PARSER_VALIDATION_TYPES.REGEX ? '/pattern/' : '$.store.book[*].author'}
            />
          </div>
        </div>

        <div className="space-y-4">
          <ToggleField
            id="conditionEnabled"
            label="Add condition"
            description="Compare the matched value against a target using a logical operator"
            checked={conditionEnabled}
            onChange={setConditionEnabled}
          />

          <Transition
            show={conditionEnabled}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-2"
          >
            <div className="rounded-md bg-gray-800/50 border border-gray-700 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SelectField
                  label="Operator"
                  value={operation}
                  onChange={setOperation}
                  options={WORKER_PATH_OPERATIONS_OPTIONS.filter(
                    (opt) => opt.value !== WORKER_PATH_OPERATIONS.NONE
                  )}
                />
                <div>
                  <InputLabel htmlFor="controlValue">Value</InputLabel>
                  <div className="mt-2">
                    <BaseTextInput
                      id="controlValue"
                      value={controlValue}
                      onChange={(e) => setControlValue(e.target.value)}
                      placeholder="e.g. 100, active, true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>

        <Disclosure>
          {({ open }) => (
            <div className="rounded-md bg-gray-800/50 border border-gray-700">
              <DisclosureButton className="flex w-full justify-between rounded-t-md px-4 py-3 text-left text-sm font-medium text-gray-300 hover:bg-gray-800 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75">
                <span>Test Validation</span>
                <ChevronUpIcon
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500`}
                />
              </DisclosureButton>
              <DisclosurePanel className="px-4 pb-4 pt-2 text-sm text-gray-400">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <InputLabel htmlFor="testContent">Test Content</InputLabel>
                      <button
                        onClick={handleResetContent}
                        className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        Reset to Original
                      </button>
                    </div>
                    <div>
                      <BaseTextArea
                        id="testContent"
                        value={testContent}
                        onChange={(e) => setTestContent(e.target.value)}
                        rows={5}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleTest} loading={testing} disabled={!expression || !testContent}>
                      Validate
                    </Button>
                  </div>
                </div>
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      </div>
    </>
  );

  if (isWizard) {
    return (
      <WizardStepLayout
        title="Response Validation"
        description="Define how to validate the data extracted from the URL."
        primaryAction={{
          label: 'Next',
          onClick: handleProceed,
          disabled: !expression
        }}
      >
        {formContent}
      </WizardStepLayout>
    );
  }

  return (
    <div>
      {formContent}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700 relative z-20">
        <Button onClick={onCancel}>Cancel</Button>
        <PrimaryButton onClick={handleProceed} disabled={!expression} saving={saving}>
          Save Changes
        </PrimaryButton>
      </div>
    </div>
  );
}

export default function ParserValidationStep() {
  const { nextStep, data } = useWizard();

  return (
    <ParserValidationForm
      initialValues={{
        validationType: data.validationType,
        expression: data.expression,
        operation: data.operation,
        controlValue: data.controlValue
      }}
      testResultData={data.testResult}
      onSubmit={(values) => nextStep(values)}
      isWizard
    />
  );
}
